// Product Mapping Validation Dashboard (Kalpana UAT round 5).
// Lists every pending flag system-wide, grouped by (flag_type, brand,
// generic) so admin can bulk-approve / bulk-reject all "Duvanta 60mg /
// Cymbalta" flags in one click instead of clicking through 30 orders.

import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { validationFlagApi } from '../api/services';
import toast from 'react-hot-toast';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Inbox, Edit2, X } from 'lucide-react';

const FLAG_LABEL = {
  case_a: 'Case A — Generic exists, brand new',
  case_b: 'Case B — Both new',
  case_c: 'Case C — Container mismatch',
  shipper: 'Shipper rule missing',
};

export default function ValidationDashboard() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  // Per-group edit buffer — only the keys with active edits live here.
  // Shape: { [groupKey]: { brand, generic, container } }
  const [edits, setEdits] = useState({});
  const [editingKey, setEditingKey] = useState(null);

  function load() {
    setLoading(true);
    validationFlagApi.pending()
      .then((res) => setFlags(res.data.flags || []))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load'))
      .finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  // Group pending flags by (flag_type, normalized brand, normalized generic).
  // Each group shows the count, sample order ids, and bulk actions.
  const groups = useMemo(() => {
    const map = new Map();
    for (const f of flags) {
      const norm = (s) => (s || '').trim().toLowerCase();
      const key = `${f.flag_type}::${norm(f.brand)}::${norm(f.generic)}::${norm(f.country)}`;
      if (!map.has(key)) {
        map.set(key, {
          key, flag_type: f.flag_type,
          brand: f.brand, generic: f.generic,
          country: f.country, master_container: f.master_container,
          flags: [],
        });
      }
      map.get(key).flags.push(f);
    }
    return Array.from(map.values()).sort((a, b) => b.flags.length - a.flags.length);
  }, [flags]);

  // Per-group edit helpers — flip into edit mode, change values, then either
  // discard with Cancel or commit via "Approve & map".
  function startEdit(g) {
    setEditingKey(g.key);
    setEdits((prev) => ({
      ...prev,
      [g.key]: {
        brand: g.brand || '',
        generic: g.generic || '',
        container: g.master_container || '',
      },
    }));
  }
  function cancelEdit(g) {
    setEditingKey((k) => (k === g.key ? null : k));
    setEdits((prev) => {
      const { [g.key]: _drop, ...rest } = prev;
      return rest;
    });
  }
  function updateEdit(key, field, value) {
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  }

  async function bulkApprove(g) {
    if (g.flag_type === 'shipper') {
      toast.error('Shipper flags need a per-flag shipper code; resolve from order detail or via Shipper rules page.');
      return;
    }
    const edit = edits[g.key] || null;
    const editing = editingKey === g.key && edit;
    const finalBrand   = editing ? (edit.brand || '').trim()   : g.brand;
    const finalGeneric = editing ? (edit.generic || '').trim() : g.generic;
    const finalContainer = editing ? (edit.container || '').trim() : null;
    if (editing) {
      if (!finalBrand || !finalGeneric) {
        toast.error('Indian brand and generic name are both required');
        return;
      }
      if (!window.confirm(`Approve & map ${g.flags.length} flag(s) with the corrected mapping:\n\n  Brand:   ${finalBrand}\n  Generic: ${finalGeneric}\n${finalContainer ? `  Container: ${finalContainer}\n` : ''}\nThe Product Mapping Master will be updated with these values.`)) return;
    } else {
      if (!window.confirm(`Approve & map ${g.flags.length} flag(s) for "${g.brand}" / "${g.generic}"?`)) return;
    }
    setBusyKey(g.key);
    try {
      const body = {
        flagType: g.flag_type,
        brand: g.brand,     // matcher always uses ORIGINAL values
        generic: g.generic, // (the edit is what we WRITE, not what we MATCH)
      };
      if (editing) {
        body.edit = {
          brand: finalBrand,
          generic: finalGeneric,
          ...(finalContainer ? { container: finalContainer } : {}),
        };
      }
      const res = await validationFlagApi.bulkApprove(body);
      toast.success(`Approved ${res.data.approved} of ${res.data.matched}`);
      cancelEdit(g);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk approve failed');
    } finally { setBusyKey(null); }
  }

  async function bulkReject(g) {
    if (!window.confirm(`Reject ${g.flags.length} flag(s) for "${g.brand}" / "${g.generic}"?`)) return;
    const note = window.prompt('Optional reason for rejection:', '') ?? null;
    setBusyKey(g.key);
    try {
      const res = await validationFlagApi.bulkReject({
        flagType: g.flag_type, brand: g.brand, generic: g.generic, note,
      });
      toast.success(`Rejected ${res.data.rejected} of ${res.data.matched}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk reject failed');
    } finally { setBusyKey(null); }
  }

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">
      <Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading flags…
    </div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <p className="text-sm text-gray-500 mb-4">
        Every pending validation flag in the system, grouped by similar issue.
        Use bulk actions when one MBO typo affected many orders.
        Click a flag count to drill into the affected orders.
      </p>

      {flags.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          <Inbox className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          No pending validation flags. Nothing to review.
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.key} className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-orange-600" />
                <span className="text-xs uppercase font-semibold tracking-wide text-gray-700">
                  {FLAG_LABEL[g.flag_type]}
                </span>
                <span className="text-xs text-gray-500 ml-auto">{g.flags.length} order(s)</span>
              </div>

              <div className="text-sm">
                {g.flag_type === 'shipper' ? (
                  <span>Country: <strong>{g.country}</strong></span>
                ) : editingKey === g.key ? (
                  <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-3xl">
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">Indian brand</span>
                      <input value={edits[g.key]?.brand ?? ''}
                             onChange={(e) => updateEdit(g.key, 'brand', e.target.value)}
                             className="w-full px-2 py-1 text-sm border border-amber-300 rounded bg-white" />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">Generic name</span>
                      <input value={edits[g.key]?.generic ?? ''}
                             onChange={(e) => updateEdit(g.key, 'generic', e.target.value)}
                             className="w-full px-2 py-1 text-sm border border-amber-300 rounded bg-white" />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] uppercase tracking-wide text-gray-500 mb-0.5">Container</span>
                      <input value={edits[g.key]?.container ?? ''}
                             onChange={(e) => updateEdit(g.key, 'container', e.target.value)}
                             placeholder="e.g. Strip, Bottle"
                             className="w-full px-2 py-1 text-sm border border-amber-300 rounded bg-white" />
                    </label>
                  </div>
                ) : (
                  <span>
                    Brand: <strong>{g.brand}</strong> · Generic: <strong>{g.generic}</strong>
                    {g.flag_type === 'case_c' && g.master_container && (
                      <> · Master container: <strong>{g.master_container}</strong></>
                    )}
                  </span>
                )}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {g.flag_type !== 'shipper' && (
                  <button onClick={() => bulkApprove(g)} disabled={busyKey === g.key}
                          className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded text-xs font-medium hover:bg-emerald-700 disabled:opacity-50">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    {editingKey === g.key ? `Approve & map ${g.flags.length} (with edits)` : `Approve all ${g.flags.length}`}
                  </button>
                )}
                {g.flag_type !== 'shipper' && editingKey !== g.key && (
                  <button onClick={() => startEdit(g)} disabled={busyKey === g.key}
                          className="flex items-center gap-1 px-3 py-1.5 bg-amber-100 text-amber-800 rounded text-xs font-medium hover:bg-amber-200 disabled:opacity-50">
                    <Edit2 className="w-3.5 h-3.5" /> Edit before approve
                  </button>
                )}
                {editingKey === g.key && (
                  <button onClick={() => cancelEdit(g)} disabled={busyKey === g.key}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 disabled:opacity-50">
                    <X className="w-3.5 h-3.5" /> Cancel edit
                  </button>
                )}
                <button onClick={() => bulkReject(g)} disabled={busyKey === g.key}
                        className="flex items-center gap-1 px-3 py-1.5 bg-rose-100 text-rose-800 rounded text-xs font-medium hover:bg-rose-200 disabled:opacity-50">
                  <XCircle className="w-3.5 h-3.5" /> Reject all {g.flags.length}
                </button>
              </div>

              <details className="mt-2">
                <summary className="text-xs text-gray-500 cursor-pointer">
                  Affected orders ({g.flags.length})
                </summary>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                  {g.flags.slice(0, 60).map((f) => (
                    <Link key={f.id} to={`/orders/${f.order_id}`}
                          className="font-mono text-blue-600 hover:underline truncate"
                          title={`${f.order_id_external} · ${f.customer_name}`}>
                      {f.order_id_external}
                    </Link>
                  ))}
                  {g.flags.length > 60 && (
                    <span className="text-gray-500 col-span-full">…and {g.flags.length - 60} more</span>
                  )}
                </div>
              </details>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
